import React, { useState, useEffect } from 'react';
import { Calendar, Play, Heart, Instagram, Youtube, Facebook, Coffee, Sparkles, Menu, X, Church, CheckCircle, Send, User } from 'lucide-react';
import { motion, LazyMotion, domAnimation } from 'framer-motion';
import PrivacyPolicy from './PrivacyPolicy';
import TermsConditions from './TermsConditions';

// Steam component for hero animation
const Steam = () => (
  <div aria-hidden className="absolute -top-8 left-1/2 transform -translate-x-1/2 flex gap-1">
    <div className="steam-dot"></div>
    <div className="steam-dot"></div>
    <div className="steam-dot"></div>
  </div>
);

// --- Mini Kokoodi Pane (subtle Q&A) ---
const KokoodiMiniPane = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState(() => {
    // restore from localStorage
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem('kokoodiPane') : null;
    return saved ? JSON.parse(saved) : [
      { role: 'assistant', text: "Welcome to Hope Cathedral's Café Chat! ☕ I'm your friendly barista assistant. Ask me about the project, the team, or how to support our divine comedy!" }
    ];
  });

  const quickPrompts = [
    "How did Hope Cathedral start?",
    "Who are the directors/producers?",
    "What's the show's tone & audience?",
    "How can I support or invest?"
  ];

  useEffect(() => {
    try { window.localStorage.setItem('kokoodiPane', JSON.stringify(messages)); } catch {}
  }, [messages]);

  // Simple knowledge map (replace with live Kokoodi later)
  function answerFor(question) {
    const q = question.toLowerCase();
    if (q.includes('start') || q.includes('begin') || q.includes('origin')) {
      return "It began as a comedy idea set inside a London café-church—where faith, humor, and community intersect. The team wanted a warm, ensemble piece that feels familiar like a local spot you love.";
    }
    if (q.includes('director') || q.includes('producer') || q.includes('team')) {
      return "Visionaries: Dr. Victor Odunlami (EP), Ayo-Dele Edwards (Producer), Directors Daniel Paquette & Gbadero Lahanmi. A seasoned group blending comedy craft with heartfelt storytelling.";
    }
    if (q.includes('tone') || q.includes('audience') || q.includes('vibe')) {
      return "Tone: warm, witty, ensemble comedy with heart—think community laughs and redemptive arcs. Audience: 18–49 primary, cross-gen appeal, strong Afro-Caribbean resonance.";
    }
    if (q.includes('support') || q.includes('invest') || q.includes('fund')) {
      return "You can back the project via the Support tiers or request the Investment Deck. Associate Producer and Executive options include credits, screening invites, and VIP access.";
    }
    if (q.includes('screening') || q.includes('november') || q.includes('date')) {
      return "The screening is on Saturday, 15 November 2025 at 16:00 GMT at Kingsway International Christian Centre (KICC), 474 Hoe Street, London E17 9AH. Get tickets on Eventbrite or check the Screening section!";
    }
    return "Here's what I can help with: origins, team, tone & audience, screening, and ways to support. Ask me anything!";
  }

  async function handleSend(text) {
    const userText = (text ?? input).trim();
    if (!userText) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userText }]);

    // Simulate async (swap this with your Kokoodi API call)
    // await fetch('/api/kokoodi', { method:'POST', body: JSON.stringify({ prompt: userText }) })
    //   .then(r => r.json()).then(({ reply }) => setMessages(prev => [...prev, { role:'assistant', text: reply }]));
    const reply = answerFor(userText);
    setTimeout(() => {
      setMessages(prev => [...prev, { role: 'assistant', text: reply }]);
    }, 300);
  }

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-4 left-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-white bg-gradient-to-br from-teal-500 to-orange-500 hover:opacity-95 transition-all hover:scale-105"
          aria-label="Open project Q&A"
        >
          <Coffee className="w-5 h-5" />
          <span className="hidden sm:inline">Ask about the project</span>
        </button>
      )}

      {/* Pane */}
      {open && (
        <div className="fixed bottom-4 left-4 z-50 w-[92vw] max-w-sm rounded-2xl border-2 border-orange-200 bg-gradient-to-b from-orange-50 to-white shadow-2xl">
          <div className="flex items-center justify-between px-4 py-3 rounded-t-2xl bg-gradient-to-r from-teal-500 to-orange-500 text-white">
            <div className="flex items-center gap-2">
              <Coffee className="w-5 h-5" />
              <span className="font-bold">Café Chat</span>
              <span className="text-xs opacity-75">☕</span>
            </div>
            <button aria-label="Close" onClick={() => setOpen(false)} className="hover:opacity-80 transition-opacity">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="max-h-80 overflow-y-auto px-4 py-3 space-y-3 bg-gradient-to-b from-white to-orange-25">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`${m.role === 'user'
                  ? 'bg-gradient-to-br from-orange-100 to-orange-200 text-orange-900 border border-orange-300'
                  : 'bg-gradient-to-br from-teal-50 to-teal-100 text-teal-900 border border-teal-200'
                } px-3 py-2 rounded-xl max-w-[80%] shadow-sm`}>
                  <div className="flex items-center gap-2 text-xs mb-1 opacity-70">
                    {m.role === 'user' ? <User className="w-3.5 h-3.5" /> : <Coffee className="w-3.5 h-3.5" />}
                    {m.role === 'user' ? 'You' : 'Barista'}
                  </div>
                  <div className="text-sm leading-relaxed">{m.text}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick prompts */}
          <div className="px-4 pb-2 flex flex-wrap gap-2">
            {quickPrompts.map(q => (
              <button
                key={q}
                onClick={() => handleSend(q)}
                className="text-xs px-3 py-1 rounded-full bg-gradient-to-r from-teal-100 to-orange-100 text-teal-800 hover:from-teal-200 hover:to-orange-200 transition-all border border-teal-200 shadow-sm"
              >
                {q}
              </button>
            ))}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); handleSend(); }}
            className="flex items-center gap-2 p-3 border-t"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about the show…"
              className="flex-1 px-3 py-2 rounded-lg border-2 border-teal-200 focus:outline-none focus:border-teal-500"
            />
            <button
              type="submit"
              aria-label="Send"
              className="p-2 rounded-lg bg-orange-500 text-white hover:bg-orange-600"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>

          <div className="px-4 py-2 text-[11px] text-teal-700 bg-gradient-to-r from-teal-50 to-orange-50 rounded-b-2xl border-t border-teal-200">
            <a href="https://www.kokoodi.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:underline">
              <img src="./kokoodi-logo-white.png" alt="Kokoodi" className="h-3 opacity-60" />
              <span>Powered by Kokoodi • Conversations brewed gently ☕</span>
            </a>
          </div>
        </div>
      )}
    </>
  );
};

const HopeCathedralLanding = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [mobileMenu, setMobileMenu] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', amount: '', tier: 'Supporter', company: ''
  });
  const [scrollFill, setScrollFill] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [heroSlide, setHeroSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [tierSlide, setTierSlide] = useState(2); // Start with Gold tier (index 2)
  const [tierTouchStart, setTierTouchStart] = useState(0);
  const [tierTouchEnd, setTierTouchEnd] = useState(0);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showInvestmentModal, setShowInvestmentModal] = useState(false);
  const [investmentEmail, setInvestmentEmail] = useState('');
  const [investmentName, setInvestmentName] = useState('');
  const [investmentMobile, setInvestmentMobile] = useState('');
  const [investmentSubmitting, setInvestmentSubmitting] = useState(false);
  const [investmentStatus, setInvestmentStatus] = useState(null);

  // Function to handle tier button click
  const handleTierSelect = (tierAmount, tierName) => {
    // Extract numeric value from amount (e.g., "£50+" -> "50")
    const numericAmount = tierAmount.replace(/[£+,]/g, '');
    
    // Update form data
    setFormData({
      ...formData,
      amount: numericAmount,
      tier: tierName
    });
    
    // Scroll to the form (below the tiers)
    setTimeout(() => {
      const supportSection = document.getElementById('support');
      if (supportSection) {
        // Scroll to the form area which is below the tiers
        const sectionTop = supportSection.getBoundingClientRect().top + window.pageYOffset;
        const formOffset = 700; // Scroll past the tiers to the form
        window.scrollTo({ top: sectionTop + formOffset, behavior: 'smooth' });
      }
    }, 100);
  };

  // Countdown to November 15, 2025 at 16:00 GMT
  // Auto-scroll to donations if URL contains /donations or #donations
  useEffect(() => {
    const path = window.location.pathname + window.location.hash;
    if (path.includes('donations') || path.includes('support')) {
      setTimeout(() => {
        const supportSection = document.getElementById('support');
        if (supportSection) {
          supportSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  useEffect(() => {
    const targetDate = new Date('2025-11-15T16:00:00Z');
    
    const timer = setInterval(() => {
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();
      
      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((difference / 1000 / 60) % 60);
        const seconds = Math.floor((difference / 1000) % 60);
        
        setTimeLeft({ days, hours, minutes, seconds });
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  // Parallax scroll effect
  useEffect(() => {
    // Scroll effect functionality can be added here if needed
  }, []);

  // Hope Meter scroll progress
  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const pct = Math.min(1, window.scrollY / max);
      setScrollFill(pct);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Hero carousel auto-rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setHeroSlide(prev => {
        const next = (prev + 1) % 3; // Cycle through 0, 1, 2
        return next;
      });
    }, heroSlide === 0 ? 10000 : 6000); // Countdown stays 10 seconds, others 6 seconds
    return () => clearInterval(interval);
  }, [heroSlide]);

  // Touch swipe handlers for carousel
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(0); // Reset
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      // Swipe left - go to next slide
      setHeroSlide(prev => (prev + 1) % 3);
    } else if (isRightSwipe) {
      // Swipe right - go to previous slide
      setHeroSlide(prev => (prev - 1 + 3) % 3);
    }
  };

  // Touch swipe handlers for tier carousel
  const onTierTouchStart = (e) => {
    setTierTouchEnd(0);
    setTierTouchStart(e.targetTouches[0].clientX);
  };

  const onTierTouchMove = (e) => {
    setTierTouchEnd(e.targetTouches[0].clientX);
  };

  const onTierTouchEnd = () => {
    if (!tierTouchStart || !tierTouchEnd) return;
    
    const distance = tierTouchStart - tierTouchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && tierSlide < tiers.length - 1) {
      setTierSlide(prev => prev + 1);
    } else if (isRightSwipe && tierSlide > 0) {
      setTierSlide(prev => prev - 1);
    }
  };

  const team = [
    { 
      name: "Dr. Victor Odunlami", 
      role: "Executive Producer", 
      bio: "Medical doctor by training, qualified 29 years ago, worked as psychiatrist for 17 years. With over 40 years passion for drama and comedies, his love for watching comedies sparked the idea of creating a short 30-minute comedy highlighting church life challenges with humor - leading to Hope Cathedral.",
      photo: "/casts/EXECUTIVE PRODUCER_DR VICTOR ODUNLAMI.JPG"
    },
    { 
      name: "Gbadero Lahanmi", 
      role: "Director", 
      bio: "Writer, director and producer who has written, directed and produced several plays, films, TV and educational content",
      photo: "/casts/DIRECTOR_GBADERO LAHANMI.JPG"
    },
    { 
      name: "Daniel Paquette", 
      role: "Director", 
      bio: "Began career with two Bonds - Sean Connery (Entrapment) and Pierce Brosnan (Tomorrow Never Dies). Now cinematographer, director, writer and producer. Recently directed acclaimed comedy Ghosted, with Camden Town and Due Dating releasing this year.",
      photo: "/casts/DANIEL PAQUETTE_DIRECTORJPG.jpg"
    },
    { 
      name: "Ayo-Dele Edwards", 
      role: "Producer", 
      bio: "Vast experience covering theatre, radio, film, and voice-over work. Creator of 'Becoming' (solo show) exploring identity, love, family relationships, and migration. Hosts 'Becoming You' podcast about creativity's role in dealing with life's ups and downs.",
      photo: "/casts/PRODUCER_Ayo-Dele.jpeg"
    }
  ];

  const cast = [
    { name: "Tayo Elesin", role: "Actress, Writer, Director", photo: "/casts/PD_s wife AGATHA_TAYO ELESIN.JPG" },
    { name: "Katie Cannon", role: "Actress", photo: "/casts/SUMMER_KATIE CANNON.jpg" },
    { name: "Andy Sarfo", role: "Actor", photo: "/casts/ASSISTANT PASTOR_ANDY SARFO.jpg" },
    { name: "Tim Anthony Ford", role: "Actor", photo: "/casts/FRANK_TIM FORDE_.jpeg" },
    { name: "Nick Bartlett", role: "Actor", photo: "/casts/ODIN_NICKBARTLETT.jpg" },
    { name: "Julius Obende", role: "Actor, Dancer, Musician", photo: "/casts/PASTOR DARE_JULIUS.OBENDE.jpeg" },
    { name: "Kelechi Eribienne", role: "Actress, Singer", photo: "/casts/APs WIFE_KelechiObichukwu-Ebribenne.jpg" },
    { name: "Joshua Kelechi Anyanwu", role: "Actor, Director", photo: "/casts/DOUGLAS_JOSHUA ANYANWU.jpg" }
  ];

  const tiers = [
    {
      name: "Bronze",
      amount: "£50+",
      perks: ["Gratitude"],
      cta: "Support",
      bgColor: "bg-amber-600"
    },
    {
      name: "Silver",
      amount: "£100+",
      perks: ["Gratitude", "Social media recognition"],
      cta: "Support",
      bgColor: "bg-gray-400"
    },
    {
      name: "Gold",
      amount: "£500+",
      perks: ["Gratitude", "Social media recognition", "Signed poster", "T-shirt"],
      cta: "Partner",
      featured: true,
      bgColor: "bg-yellow-500"
    },
    {
      name: "Platinum",
      amount: "£1,000+",
      perks: ["Gratitude", "Social media recognition", "Signed poster", "T-shirt", "Associate Producer credits"],
      cta: "Partner",
      bgColor: "bg-gradient-to-br from-slate-700 to-slate-500"
    },
    {
      name: "Diamond",
      amount: "£2,500+",
      perks: ["Gratitude", "Social media recognition", "Signed poster", "T-shirt", "Company logo in credits"],
      cta: "Invest",
      bgColor: "bg-cyan-400"
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);

    // Prepare payload for Web3Forms
    const payload = {
      access_key: 'b5772ea8-6827-4835-93d2-36978f29491f',
      name: formData.name,
      email: formData.email,
      amount: formData.amount,
      tier: formData.tier,
      company: formData.company
    };

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const json = await res.json();

      if (res.ok && json.success) {
        setSubmitStatus({ type: 'success', message: 'Thanks! Your pledge has been received. We\'ll be in touch soon.' });
        setFormData({ name: '', email: '', amount: '', tier: tiers[0].name, company: '' });
      } else {
        console.error('Web3Forms error', json);
        setSubmitStatus({ type: 'error', message: 'There was a problem submitting your pledge. Please try again or contact us directly.' });
      }
    } catch (err) {
      console.error('Submit error', err);
      setSubmitStatus({ type: 'error', message: 'Network error — please check your connection and try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayPalDonation = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.email) {
      setSubmitStatus({ type: 'error', message: 'Please enter your name and email before donating.' });
      return;
    }

    // Open PayPal immediately to prevent mobile popup blocking
    const paypalUrl = `https://www.paypal.com/ncp/payment/RMJ3KYKVNRVHA${formData.amount ? `?amount=${formData.amount}` : ''}`;
    window.open(paypalUrl, '_blank');

    setSubmitting(true);
    setSubmitStatus(null);

    // Submit contact info to Web3Forms in background
    const payload = {
      access_key: 'b5772ea8-6827-4835-93d2-36978f29491f',
      name: formData.name,
      email: formData.email,
      amount: formData.amount,
      tier: formData.tier,
      company: formData.company,
      subject: 'PayPal Donation Intent'
    };

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const json = await res.json();

      if (res.ok && json.success) {
        setSubmitStatus({ type: 'success', message: 'Opening PayPal... Your contact info has been saved.' });
        // Don't clear form immediately so user can see the success message
        setTimeout(() => {
          setFormData({ name: '', email: '', amount: '', tier: tiers[0].name, company: '' });
          setSubmitStatus(null);
        }, 3000);
      } else {
        console.error('Web3Forms error', json);
        setSubmitStatus({ type: 'error', message: 'Could not save your info. Please try again.' });
        // Still opened PayPal, so just log the error
      }
    } catch (err) {
      console.error('Submit error', err);
      setSubmitStatus({ type: 'error', message: 'Network error — your info may not have been saved.' });
      // Still opened PayPal, so just log the error
    } finally {
      setSubmitting(false);
    }
  };

  const handleInvestmentDeckRequest = async (e) => {
    e.preventDefault();
    setInvestmentSubmitting(true);
    setInvestmentStatus(null);

    const payload = {
      access_key: 'b5772ea8-6827-4835-93d2-36978f29491f',
      name: investmentName,
      email: investmentEmail || 'Not provided',
      phone: investmentMobile || 'Not provided',
      subject: 'Investment Deck Request',
      message: `Investment Deck has been requested by ${investmentName}${investmentEmail ? ` (${investmentEmail})` : ''}${investmentMobile ? ` - Mobile: ${investmentMobile}` : ''}`
    };

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const json = await res.json();

      if (res.ok && json.success) {
        setInvestmentStatus({ type: 'success', message: 'Thank you! Your investment deck request has been received. We\'ll send it to your email shortly.' });
        setInvestmentEmail('');
        setInvestmentName('');
        setInvestmentMobile('');
        setTimeout(() => {
          setShowInvestmentModal(false);
          setInvestmentStatus(null);
        }, 3000);
      } else {
        console.error('Web3Forms error', json);
        setInvestmentStatus({ type: 'error', message: 'There was a problem. Please try again or email us directly at hopecathedralshow@gmail.com' });
      }
    } catch (err) {
      console.error('Submit error', err);
      setInvestmentStatus({ type: 'error', message: 'Network error — please check your connection and try again.' });
    } finally {
      setInvestmentSubmitting(false);
    }
  };

  // Wavy pattern SVG
  const WavyPattern = ({ className = "" }) => (
    <svg className={className} viewBox="0 0 1440 120" preserveAspectRatio="none">
      <path d="M0,0 C240,60 480,90 720,90 C960,90 1200,60 1440,0 L1440,120 L0,120 Z" fill="currentColor" />
    </svg>
  );

  const DiagonalStripes = ({ className = "" }) => (
    <div className={`absolute inset-0 ${className}`}>
      <div className="absolute inset-0" style={{
        backgroundImage: `repeating-linear-gradient(
          45deg,
          transparent,
          transparent 20px,
          rgba(255,140,0,0.1) 20px,
          rgba(255,140,0,0.1) 40px
        )`
      }} />
    </div>
  );

  return (
    <LazyMotion features={domAnimation}>
      <div className="min-h-screen bg-white">
      {/* Header with teal background */}
      <header className="sticky top-0 z-50 bg-teal-500 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center">
                <Church className="w-8 h-8 text-white" />
                <Coffee className="w-6 h-6 text-white -ml-2" />
              </div>
              <div>
                <div className="font-black text-2xl text-white">
                  HOPE CATHEDRAL
                </div>
                <div className="text-xs text-teal-100">
                  Hekspace Studios · Kherut Films · Reel, Flicks & Art
                </div>
              </div>
            </div>
            
            <nav className="hidden md:flex items-center gap-6 text-white">
              <a href="#about" className="hover:text-orange-300 transition-colors font-medium">About</a>
              <a href="#trailer" className="hover:text-orange-300 transition-colors font-medium">Trailer</a>
              <a href="#team" className="hover:text-orange-300 transition-colors font-medium">Team</a>
              <a href="#cast" className="hover:text-orange-300 transition-colors font-medium">Cast</a>
              <a href="#support" className="hover:text-orange-300 transition-colors font-medium">Support</a>
              <a href="#screening" className="hover:text-orange-300 transition-colors font-medium">Screening</a>
            </nav>

            <div className="flex items-center gap-3">
              <a href="#support" className="hidden sm:inline-flex px-5 py-2 bg-orange-500 text-white rounded-full font-bold hover:bg-orange-600 transition-all shadow-lg">
                Support the Film
              </a>

              <button
                onClick={() => setMobileMenu(!mobileMenu)}
                className="md:hidden p-2 bg-teal-600 rounded-lg text-white"
              >
                {mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenu && (
            <div className="md:hidden mt-4 pb-4 border-t border-teal-400">
              <div className="pt-4 space-y-2">
                <a href="#about" className="block text-white hover:text-orange-300 py-2">About</a>
                <a href="#trailer" className="block text-white hover:text-orange-300 py-2">Trailer</a>
                <a href="#team" className="block text-white hover:text-orange-300 py-2">Team</a>
                <a href="#cast" className="block text-white hover:text-orange-300 py-2">Cast</a>
                <a href="#support" className="block text-white hover:text-orange-300 py-2">Support</a>
                <a href="#screening" className="block text-white hover:text-orange-300 py-2">Screening</a>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section with Orange/Coral gradient */}
      <section className="relative overflow-hidden bg-gradient-to-br from-orange-400 via-orange-500 to-pink-500">
        <DiagonalStripes className="opacity-20" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-20 md:py-32">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur rounded-full px-4 py-2 text-white mb-6">
                <span className="font-bold">Comedy TV Series</span>
                <span>•</span>
                <span>Dunamis - On-Stage Productions</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-black text-white mb-6">
                <span className="flex items-center gap-2">
                  <Church className="w-12 h-12 md:w-16 md:h-16" />
                  HOPE
                </span>
                <span className="text-pink-200 neon-flicker">CATHEDRAL</span>
              </h1>
              
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                In a tiny London café that serves divine lattes and heavenly pastries, a group of church leaders and workers are on a mission from God – to grow their congregation. But with cappuccino catastrophes, latte-loving locals, and a café owner who everyone thinks is a commander of military platoon, divine intervention is their only hope.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <a href="#trailer" className="px-6 py-3 bg-white text-orange-500 rounded-full font-bold hover:bg-gray-100 transform hover:scale-105 transition-all shadow-xl flex items-center gap-2">
                  <Play className="w-5 h-5" />
                  Watch Trailer
                </a>
                <a href="#support" className="px-6 py-3 bg-teal-500 text-white rounded-full font-bold hover:bg-teal-600 transform hover:scale-105 transition-all shadow-xl flex items-center gap-2">
                  <Heart className="w-5 h-5" />
                  Support Production
                </a>
              </div>
            </div>

            <div 
              className="relative h-[500px] md:h-[600px]"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              {/* Slide 1: Countdown */}
              <div 
                className={`absolute inset-0 bg-gradient-to-br from-teal-500 to-teal-600 rounded-3xl p-4 shadow-2xl transition-all duration-1000 ease-in-out ${
                  heroSlide === 0 ? 'translate-x-0 opacity-100' : (heroSlide > 0 ? '-translate-x-full opacity-0 pointer-events-none' : 'translate-x-full opacity-0 pointer-events-none')
                }`}
              >
                <div className="bg-orange-500 rounded-2xl p-6 md:p-8 h-full flex flex-col justify-center items-center text-white relative overflow-hidden">
                  {/* Coffee steam decorative elements */}
                  <div className="absolute top-4 left-8 opacity-20">
                    <Coffee className="w-12 h-12" />
                  </div>
                  <div className="absolute top-8 right-8 opacity-20">
                    <Church className="w-12 h-12" />
                  </div>
                  
                  <div className="relative mb-4">
                    <Steam />
                    <h3 className="text-2xl md:text-4xl font-black text-center">
                      THE GRAND OPENING
                    </h3>
                  </div>
                  
                  <p className="text-base md:text-xl font-bold mb-6 text-center">
                    Fresh Brews & First Views in...
                  </p>
                  
                  <div className="grid grid-cols-4 gap-3 md:gap-4 mb-8 w-full max-w-lg">
                    {[
                      {label: 'Days', value: timeLeft.days},
                      {label: 'Hours', value: timeLeft.hours},
                      {label: 'Mins', value: timeLeft.minutes},
                      {label: 'Secs', value: timeLeft.seconds}
                    ].map((unit) => (
                      <div key={unit.label} className="bg-white/90 backdrop-blur rounded-xl p-3 md:p-4 text-center shadow-lg hover:scale-105 transition-transform">
                        <p className="text-3xl md:text-5xl font-black text-orange-500">{String(unit.value).padStart(2, '0')}</p>
                        <p className="text-xs md:text-sm font-bold text-teal-600 mt-1">{unit.label}</p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="text-center bg-white/10 backdrop-blur rounded-xl px-6 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <Calendar className="w-5 h-5" />
                      <p className="text-lg md:text-xl font-bold">Saturday, 15 November 2025</p>
                    </div>
                    <p className="text-sm md:text-base mt-1">16:00 GMT • KICC London</p>
                  </div>
                  
                  {/* Slanted CTA Button */}
                  <a 
                    href="https://www.eventbrite.com/e/launch-of-hope-cathedral-christian-comedy-tv-show-tickets-1753347632969"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute bottom-4 right-4 md:bottom-6 md:right-6 z-50 px-6 py-3 md:px-8 md:py-4 bg-teal-600 text-white rounded-xl font-black hover:bg-teal-700 active:bg-teal-800 transition-all shadow-2xl transform rotate-3 hover:rotate-0 hover:scale-110 flex items-center gap-2 border-4 border-white cursor-pointer"
                    style={{ transformOrigin: 'center' }}
                  >
                    <Calendar className="w-5 h-5" />
                    <span className="text-base md:text-lg">Get Tickets</span>
                  </a>
                </div>
              </div>

              {/* Slide 2: Flyer */}
              <div 
                className={`absolute inset-0 bg-gradient-to-br from-teal-500 to-teal-600 rounded-3xl p-4 shadow-2xl transition-all duration-1000 ease-in-out flex items-center justify-center ${
                  heroSlide === 1 ? 'translate-x-0 opacity-100' : (heroSlide > 1 ? '-translate-x-full opacity-0 pointer-events-none' : 'translate-x-full opacity-0 pointer-events-none')
                }`}
              >
                <img 
                  src="/hopeCathHero2.jpg" 
                  alt="Hope Cathedral - Comedy TV Series" 
                  className="max-h-full max-w-full object-contain rounded-2xl"
                  loading="eager"
                  decoding="async"
                />
              </div>

              {/* Slide 3: Coffee Cup */}
              <div 
                className={`absolute inset-0 bg-gradient-to-br from-teal-500 to-teal-600 rounded-3xl p-8 shadow-2xl transition-all duration-1000 ease-in-out flex items-center justify-center ${
                  heroSlide === 2 ? 'translate-x-0 opacity-100' : (heroSlide === 0 ? 'translate-x-full opacity-0 pointer-events-none' : '-translate-x-full opacity-0 pointer-events-none')
                }`}
              >
                <div className="bg-orange-500 rounded-2xl p-8 w-full max-w-md shadow-xl">
                  <div className="text-center text-white">
                    <div className="relative mb-4">
                      <Steam />
                      <Coffee className="w-32 h-32 mx-auto" />
                    </div>
                    <h2 className="text-3xl font-bold mb-2">Hope Cathedral</h2>
                    <p className="text-lg mb-4">A Comedy TV Series</p>
                    <div className="text-sm bg-white/20 backdrop-blur rounded-full px-4 py-2 inline-block">
                      Coming Soon
                    </div>
                  </div>
                </div>
              </div>

              {/* Carousel indicators */}
              <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2">
                <button 
                  onClick={() => setHeroSlide(0)}
                  className={`w-3 h-3 rounded-full transition-all ${heroSlide === 0 ? 'bg-orange-500 w-8' : 'bg-white/50'}`}
                  aria-label="Show countdown"
                />
                <button 
                  onClick={() => setHeroSlide(1)}
                  className={`w-3 h-3 rounded-full transition-all ${heroSlide === 1 ? 'bg-orange-500 w-8' : 'bg-white/50'}`}
                  aria-label="Show flyer"
                />
                <button 
                  onClick={() => setHeroSlide(2)}
                  className={`w-3 h-3 rounded-full transition-all ${heroSlide === 2 ? 'bg-orange-500 w-8' : 'bg-white/50'}`}
                  aria-label="Show coffee cup"
                />
              </div>
            </div>
          </div>
        </div>

        <WavyPattern className="absolute bottom-0 left-0 right-0 text-white h-20" />
      </section>

      {/* Trailer Section */}
      <motion.section 
        id="trailer" 
        className="py-20 bg-gray-900 text-white relative"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="bg-orange-500 text-white inline-block px-6 py-2 rounded-full mb-6 font-bold">
            OFFICIAL TRAILER
          </div>
          
          <h2 className="text-4xl md:text-5xl font-black mb-8">
            See Hope Cathedral Come to Life
          </h2>
          
          <div className="bg-white rounded-3xl p-2 max-w-4xl mx-auto">
            <div className="bg-black rounded-2xl overflow-hidden aspect-video relative">
              {/* Option 1: YouTube Embed (Recommended) - Replace with your YouTube URL */}
              {/* 
              <iframe 
                className="w-full h-full"
                src="https://www.youtube.com/embed/YOUR_VIDEO_ID"
                title="Hope Cathedral Trailer"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
              */}
              
              {/* Local Video with Enhanced Controls */}
              <video 
                controls 
                controlsList="nodownload"
                className="w-full h-full object-cover"
                poster="https://images.unsplash.com/photo-1478720568477-b956dc04be19?q=80&w=1600&auto=format&fit=crop&ixlib=rb-4.0.3"
                preload="metadata"
                style={{ backgroundColor: '#000' }}
                onPlay={() => setVideoPlaying(true)}
                onPause={() => setVideoPlaying(false)}
                onEnded={() => setVideoPlaying(false)}
              >
                <source src="/trailer.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              
              {/* Play Button Overlay - hides when video plays */}
              {!videoPlaying && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="bg-orange-500/20 backdrop-blur-sm rounded-full p-8 border-4 border-white/50">
                    <Play className="w-16 h-16 text-white fill-white" />
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="text-center mt-8">
            <p className="text-xl text-gray-300 mb-6">
              Get a taste of the divine comedy coming to your screens
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="#support" className="px-6 py-3 bg-orange-500 text-white rounded-full font-bold hover:bg-orange-600 transition-all flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Support the Series
              </a>
              <button 
                onClick={() => setShowInvestmentModal(true)}
                className="px-6 py-3 bg-teal-500 text-white rounded-full font-bold hover:bg-teal-600 transition-all"
              >
                Request Investment Deck
              </button>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Plot Summary Section */}
      <section id="about" className="py-20 bg-teal-500 text-white relative">
        <DiagonalStripes className="opacity-10" />
        
        <div className="relative z-10 max-w-6xl mx-auto px-4">
          <div className="bg-orange-500 text-white inline-block px-6 py-2 rounded-full mb-6 font-bold">
            PLOT SUMMARY
          </div>
          
          <h2 className="text-4xl md:text-5xl font-black mb-8">
            HOPE CATHEDRAL - CHURCH IN A CAFÉ
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4 text-lg">
              <p>
                In a tiny London café that serves divine lattes and heavenly pastries, a group of church leaders and workers are on a mission from God – to grow their congregation.
              </p>
              <p>
                But with cappuccino catastrophes, latte-loving locals, and a café owner who everyone thinks is a commander of military platoon, divine intervention is their only hope.
              </p>
              <p>
                Throughout the series, the café becomes a hilarious battleground for faith, politics, friendship, and foam art. With every spilled cappuccino and mixed-up order, the church leaders and café regulars learn that community is built one cup at a time – and that sometimes, the best way to grow a church is to laugh together.
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 hover:scale-105 transition-transform">
                <Coffee className="w-12 h-12 mb-2" />
                <h3 className="font-bold">Divine Coffee</h3>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 hover:scale-105 transition-transform">
                <Church className="w-12 h-12 mb-2" />
                <h3 className="font-bold">Sacred Space</h3>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 hover:scale-105 transition-transform">
                <Heart className="w-12 h-12 mb-2" />
                <h3 className="font-bold">Community</h3>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 hover:scale-105 transition-transform">
                <Sparkles className="w-12 h-12 mb-2" />
                <h3 className="font-bold">Comedy</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparisons Section */}
      <section className="py-20 bg-white relative">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-orange-500 text-white inline-block px-6 py-2 rounded-full mb-6 font-bold">
            COMPARISONS
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-teal-500 text-white rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-4">Like Desmond's</h3>
              <p className="text-lg">
                Church in a Café features a diverse and vibrant community, where the characters' interactions create a rich tapestry of humor and heart. The show captures the essence of community spirit and the comedic potential of everyday life.
              </p>
            </div>
            
            <div className="bg-pink-500 text-white rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-4">Like Cheers</h3>
              <p className="text-lg">
                The café in Church in a Café is the central hub where a quirky group of regulars gather, each bringing their own brand of humor and unique perspective to the mix. The café becomes a barroom of sorts - where everybody knows your name.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-20 bg-orange-50 relative">
        <DiagonalStripes className="opacity-5" />
        
        <div className="relative z-10 max-w-6xl mx-auto px-4">
          <div className="bg-orange-500 text-white inline-block px-6 py-2 rounded-full mb-6 font-bold">
            TARGET AUDIENCE
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Primary Audience</h2>
              <p className="text-lg mb-6 text-gray-700">
                Adults aged 18-49, particularly those of Afro-Caribbean descent. These viewers appreciate comedy series that incorporate elements of drama, relatable characters, and thought-provoking themes.
              </p>
              
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Secondary Audience</h2>
              <p className="text-lg text-gray-700">
                Teens aged 14-17 and adults 50-65+. These viewers enjoy clever humor, engaging storylines, and characters that reflect contemporary aesthetics, making them ideal audiences for the lighthearted yet meaningful content of the series.
              </p>
            </div>
            
            <div className="bg-teal-500 text-white rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-4">The series caters to:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-6 h-6 mt-0.5" />
                  <span>Comedy lovers</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-6 h-6 mt-0.5" />
                  <span>Drama enthusiasts</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-6 h-6 mt-0.5" />
                  <span>Café culture fans</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-6 h-6 mt-0.5" />
                  <span>Faith communities</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-20 bg-orange-50 relative overflow-hidden">
        <DiagonalStripes className="opacity-5" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4">
          <div className="bg-orange-500 text-white inline-block px-6 py-2 rounded-full mb-6 font-bold">
            MEET THE VISIONARIES
          </div>
          
          <h2 className="text-4xl md:text-5xl font-black mb-12">The Creative Team</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {team.map((member) => (
              <div key={member.name} className="bg-teal-500 rounded-2xl p-6 text-white hover:scale-105 transition-transform">
                <div className="flex items-start gap-4">
                  {/* Team Image Container */}
                  <div className="w-20 h-20 rounded-xl overflow-hidden bg-white/20 border-3 border-white/30 flex-shrink-0">
                    {member.photo.startsWith('http') || member.photo.startsWith('/') ? (
                      <img 
                        src={member.photo} 
                        alt={member.name}
                        className="w-full h-full object-cover"
                        style={{
                          objectPosition: member.name === 'Daniel Paquette' || member.name === 'Ayo-Dele Edwards' 
                            ? 'center 20%' 
                            : member.name === 'Dr. Victor Odunlami'
                            ? 'center 40%'
                            : 'center center'
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl">
                        {member.photo}
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="bg-orange-500 text-white inline-block px-3 py-1 rounded-full text-sm font-bold mb-2">
                      {member.role}
                    </div>
                    <h3 className="text-2xl font-bold mb-2">{member.name}</h3>
                    <p className="text-white/90">{member.bio}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cast Section */}
      <section id="cast" className="py-20 bg-teal-500 text-white relative">
        <DiagonalStripes className="opacity-10" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4">
          <div className="bg-orange-500 text-white inline-block px-6 py-2 rounded-full mb-6 font-bold">
            PROPOSED CAST
          </div>
          
          <h2 className="text-4xl md:text-5xl font-black mb-12">The Stellar Ensemble</h2>
          
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            {cast.map((member, idx) => (
              <div key={member.name} className={`rounded-2xl p-6 text-center hover:scale-105 transition-transform ${
                idx % 2 === 0 ? 'bg-pink-500' : 'bg-orange-500'
              }`}>
                {/* Image Container - This is where headshots will go */}
                <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden bg-white/20 border-4 border-white/30">
                  {member.photo.startsWith('http') || member.photo.startsWith('/') ? (
                    <img 
                      src={member.photo} 
                      alt={member.name}
                      className="w-full h-full object-cover"
                      style={{
                        transform: member.name === 'Kelechi Eribienne' ? 'scale(1.5)' : 
                                   member.name === 'Andy Sarfo' ? 'scale(2.5)' : 'none',
                        objectPosition: member.name === 'Katie Cannon' ? 'center 25%' : 
                                       member.name === 'Joshua Kelechi Anyanwu' ? 'center 15%' :
                                       member.name === 'Kelechi Eribienne' ? 'center 75%' :
                                       member.name === 'Andy Sarfo' ? '60% -10%' : 'center center'
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-3xl">
                      {member.photo}
                    </div>
                  )}
                </div>
                <h3 className="font-bold text-lg mb-1">{member.name}</h3>
                <p className="text-sm text-white/90">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Director's Table Gallery */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-orange-500 text-white inline-block px-6 py-2 rounded-full mb-6 font-bold">
            DIRECTOR'S TABLE
          </div>
          <div
            className="rounded-3xl p-6 md:p-10 border border-orange-200"
            style={{
              backgroundImage:
                'linear-gradient(0deg, rgba(255,255,255,.9), rgba(255,255,255,.9)), url(https://images.unsplash.com/photo-1521017432531-fbd92d768814?q=80&w=1600&auto=format&fit=crop&ixlib=rb-4.0.3)',
              backgroundSize: 'cover',
            }}
          >
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title:'Miracle Latte', note:'Foam art gag in Scene 3', color:'bg-pink-500' },
                { title:'Opening Wide', note:'Café establishing, golden hour', color:'bg-teal-500' },
                { title:'Prayer Over Machine', note:'Steam hiss + laughter beat', color:'bg-orange-500' },
                { title:'Community Table', note:'Ensemble banter, overlapping lines', color:'bg-teal-500' },
                { title:'Pastor\'s Aside', note:'Heart line lands before tag', color:'bg-pink-500' },
                { title:'Final Button', note:'Hope punchline + applause', color:'bg-orange-500' },
              ].map((c, i)=>(
                <div
                  key={i}
                  className={`group ${c.color} text-white rounded-2xl p-5 shadow-lg transition-transform hover:-rotate-1 hover:scale-[1.02]`}
                >
                  <div className="aspect-[4/3] rounded-xl bg-white/20 grid place-items-center text-5xl mb-4">🎬</div>
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold">{c.title}</h3>
                    <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Storyboard</span>
                  </div>
                  <p className="text-white/90 text-sm mt-2">{c.note}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="py-20 bg-gradient-to-br from-orange-400 to-pink-500 relative">
        <DiagonalStripes className="opacity-10" />
        
        <div className="relative z-10 max-w-6xl mx-auto px-4">
          <div className="bg-white text-orange-500 inline-block px-6 py-2 rounded-full mb-6 font-bold">
            FUNDRAISING
          </div>
          
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">Be Part of the Journey</h2>
          <p className="text-xl text-white/90 mb-12 max-w-3xl">
            Your support brings authentic, faith-filled storytelling to the screen. Choose a tier or request the investment deck.
          </p>
          
          {/* Mobile: Horizontal Scrolling Tiers (3 cards visible) */}
          <div className="lg:hidden mb-12 relative z-20">
            <div 
              className="relative overflow-x-hidden py-4"
              onTouchStart={onTierTouchStart}
              onTouchMove={onTierTouchMove}
              onTouchEnd={onTierTouchEnd}
            >
              <div 
                className="flex gap-4 transition-transform duration-500 ease-out"
                style={{ 
                  transform: `translateX(calc(20vw - ${tierSlide * 64}vw - ${tierSlide * 16}px))`
                }}
              >
                {tiers.map((tier, index) => (
                  <div 
                    key={tier.name} 
                    className={`flex-shrink-0 w-[60vw] transition-all duration-500 ${
                      index === tierSlide 
                        ? 'scale-100' 
                        : 'scale-95'
                    }`}
                  >
                    <div className={`${tier.bgColor} text-white rounded-xl p-5 h-full relative overflow-hidden ${
                      tier.featured && index === tierSlide ? 'ring-2 ring-white shadow-xl' : ''
                    }`}>
                      {/* Decorative corner badge */}
                      <div className="absolute top-0 right-0 w-12 h-12 overflow-hidden">
                        <div className={`absolute top-2 right-[-24px] w-24 text-center rotate-45 ${
                          tier.featured ? 'bg-white text-orange-500' : 'bg-white/20'
                        } text-[8px] font-bold py-0.5`}>
                          {tier.featured ? 'HOT' : `T${index + 1}`}
                        </div>
                      </div>
                      
                      <div className="text-center mb-4">
                        <h3 className="text-xl font-bold mb-1">{tier.name}</h3>
                        <div className="text-3xl font-black">{tier.amount}</div>
                      </div>
                      
                      <ul className="space-y-2 mb-5">
                        {tier.perks.map((perk) => (
                          <li key={perk} className="flex gap-2 items-start">
                            <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span className="text-sm leading-snug">{perk}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <button 
                        type="button"
                        onClick={() => handleTierSelect(tier.amount, tier.name)}
                        className="w-full py-2.5 bg-white/20 backdrop-blur rounded-full text-sm font-bold hover:bg-white/30 transition-all"
                      >
                        {tier.cta}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Tier Indicators */}
            <div className="flex justify-center gap-2 mt-4">
              {tiers.map((tier, index) => (
                <button
                  key={index}
                  onClick={() => setTierSlide(index)}
                  className={`h-1.5 rounded-full transition-all ${
                    index === tierSlide ? 'w-6 bg-white' : 'w-1.5 bg-white/40'
                  }`}
                  aria-label={`Go to ${tier.name} tier`}
                />
              ))}
            </div>
          </div>

          {/* Desktop: Grid Layout with Featured Card Elevated */}
          <div className="hidden lg:grid grid-cols-5 gap-6 mb-12 items-center">
            {tiers.map((tier, index) => (
              <div 
                key={tier.name} 
                className={`${tier.bgColor} text-white rounded-2xl p-5 hover:scale-105 transition-all duration-300 ${
                  tier.featured ? 'scale-110 shadow-2xl ring-4 ring-white z-10' : ''
                } relative overflow-hidden`}
              >
                {/* Decorative corner badge */}
                <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden">
                  <div className={`absolute top-3 right-[-32px] w-32 text-center rotate-45 ${
                    tier.featured ? 'bg-white text-orange-500' : 'bg-white/20'
                  } text-[10px] font-bold py-1`}>
                    {tier.featured ? 'POPULAR' : `Tier ${index + 1}`}
                  </div>
                </div>
                
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold mb-1">{tier.name}</h3>
                  <div className="text-2xl font-black">{tier.amount}</div>
                </div>
                
                <ul className="space-y-2 mb-6 min-h-[160px]">
                  {tier.perks.map((perk) => (
                    <li key={perk} className="flex gap-2 items-start text-sm">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>
                
                <button 
                  type="button"
                  onClick={() => handleTierSelect(tier.amount, tier.name)}
                  className="w-full py-2.5 bg-white/20 backdrop-blur rounded-full font-bold hover:bg-white/30 transition-all text-sm"
                >
                  {tier.cta}
                </button>
              </div>
            ))}
          </div>
          
          {/* PayPal Donation & Partnership Inquiry */}
          <div className="max-w-2xl mx-auto bg-white rounded-3xl p-8 shadow-2xl">
            <h3 className="text-2xl font-bold text-teal-600 mb-2">Complete Your Support</h3>
            <p className="text-gray-600 mb-6">Enter your details and choose to donate instantly or submit a pledge inquiry</p>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <input 
                  required 
                  type="text"
                  placeholder="Full name" 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="px-4 py-3 rounded-xl border-2 border-teal-200 focus:border-teal-500 focus:outline-none"
                />
                <input 
                  required 
                  type="email" 
                  placeholder="Email address" 
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="px-4 py-3 rounded-xl border-2 border-teal-200 focus:border-teal-500 focus:outline-none"
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <input 
                  type="number" 
                  min="25" 
                  step="5" 
                  placeholder="Amount (£)" 
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  className="px-4 py-3 rounded-xl border-2 border-teal-200 focus:border-teal-500 focus:outline-none"
                />
                <select 
                  value={formData.tier}
                  onChange={(e) => setFormData({...formData, tier: e.target.value})}
                  className="px-4 py-3 rounded-xl border-2 border-teal-200 focus:border-teal-500 focus:outline-none"
                >
                  {tiers.map(tier => <option key={tier.name} value={tier.name}>{tier.name}</option>)}
                </select>
              </div>
              
              <input 
                type="text"
                placeholder="Company/Organization (optional)" 
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border-2 border-teal-200 focus:border-teal-500 focus:outline-none"
              />
              
              {submitStatus && (
                <div className={`p-4 rounded-xl ${submitStatus.type === 'success' ? 'bg-green-50 text-green-800 border-2 border-green-200' : 'bg-red-50 text-red-800 border-2 border-red-200'}`}>
                  <p className="text-sm font-medium">{submitStatus.message}</p>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={handlePayPalDonation}
                  disabled={submitting}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold transition-all shadow-lg ${
                    submitting
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-[#0070BA] text-white hover:bg-[#005EA6] hover:scale-105'
                  }`}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.76-4.852a.932.932 0 0 1 .922-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.844-4.463z"/>
                  </svg>
                  {submitting ? 'Processing...' : (formData.amount ? `Donate £${formData.amount} via PayPal` : 'Donate via PayPal')}
                </button>
                
                <button 
                  type="submit"
                  disabled={submitting}
                  className={`flex-1 py-3 rounded-full font-bold transition-all ${
                    submitting 
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                      : 'bg-teal-600 text-white hover:bg-teal-700'
                  }`}
                >
                  {submitting ? 'Submitting...' : 'Submit Pledge/Inquiry'}
                </button>
              </div>
            </form>
            
            <p className="text-xs text-gray-500 mt-4 text-center">
              PayPal donations are instant. Pledges and partnership inquiries will be reviewed by our team.
            </p>
          </div>
        </div>
      </section>

      {/* Screening Section */}
      <section id="screening" className="py-20 bg-pink-500 text-white relative">
        <DiagonalStripes className="opacity-10" />
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 text-center">
          <div className="bg-white text-pink-500 inline-block px-6 py-2 rounded-full mb-6 font-bold">
            SCREENING COUNTDOWN
          </div>
          
          <h2 className="text-4xl md:text-5xl font-black mb-12">
            HOPE CATHEDRAL: A NEW BEGINNING,<br />
            FRESH GROUNDS, FIRST SIPS,<br />
            ESPRESSO BEGINNINGS, NEW BREWS
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mb-12">
            {[
              {label: 'Days', value: timeLeft.days},
              {label: 'Hours', value: timeLeft.hours},
              {label: 'Minutes', value: timeLeft.minutes},
              {label: 'Seconds', value: timeLeft.seconds}
            ].map((unit) => (
              <div key={unit.label} className="bg-white/20 backdrop-blur rounded-2xl p-6 hover:scale-105 transition-transform">
                <p className="text-5xl font-black">{String(unit.value).padStart(2, '0')}</p>
                <p className="text-white/90 mt-2">{unit.label}</p>
              </div>
            ))}
          </div>
          
          {/* Event Details */}
          <div className="max-w-3xl mx-auto mb-8">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 mb-6">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Calendar className="w-6 h-6" />
                <h3 className="text-2xl font-bold">Saturday, 15 November 2025</h3>
              </div>
              <p className="text-xl mb-4">16:00 - 19:00 GMT</p>
              
              <div className="border-t border-white/20 my-6"></div>
              
              <div className="space-y-4 text-lg">
                <div>
                  <p className="font-bold text-orange-200 mb-1">Venue</p>
                  <p>Kingsway International Christian Centre</p>
                  <p className="text-white/90">474 Hoe Street, London E17 9AH</p>
                </div>
                
                <div className="border-t border-white/20 my-4"></div>
                
                <div>
                  <p className="font-bold text-orange-200 mb-2">Event Schedule</p>
                  <div className="text-left max-w-md mx-auto space-y-2 text-base">
                    <p><span className="font-semibold">16:00 - 17:00</span> • Drinks Reception</p>
                    <p><span className="font-semibold">17:00 - 17:15</span> • Introduction from Executive Producer</p>
                    <p><span className="font-semibold">17:15 - 17:45</span> • Screening of Hope Cathedral</p>
                    <p><span className="font-semibold">17:45 - 18:15</span> • Q&A with Director, Producer & Cast</p>
                    <p><span className="font-semibold">18:30 - 19:30</span> • Networking</p>
                  </div>
                </div>
              </div>
            </div>
            
            <p className="text-xl mb-6">
              Join us for an unforgettable evening of laughter, inspiration, and community as we unveil Hope Cathedral!
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://www.eventbrite.com/e/launch-of-hope-cathedral-christian-comedy-tv-show-tickets-1753347632969"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white text-pink-500 rounded-full font-bold hover:bg-gray-100 transition-all flex items-center gap-2 shadow-xl"
            >
              <Calendar className="w-5 h-5" />
              Get Tickets on Eventbrite
            </a>
            <a 
              href="mailto:hopecathedralshow@gmail.com?subject=Screening%20RSVP"
              className="px-8 py-4 bg-orange-500 text-white rounded-full font-bold hover:bg-orange-600 transition-all flex items-center gap-2 shadow-xl"
            >
              <Heart className="w-5 h-5" />
              RSVP via Email
            </a>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 bg-orange-50">
        <div className="max-w-xl mx-auto px-4 text-center">
          <div className="bg-orange-500 text-white inline-block px-6 py-2 rounded-full mb-6 font-bold">
            STAY CONNECTED
          </div>
          
          <h2 className="text-3xl md:text-4xl font-black mb-4">Get Updates</h2>
          <p className="text-gray-600 mb-8">Subscribe for casting news, teasers, and screening details</p>
          
          <form onSubmit={(e) => { e.preventDefault(); alert('Thanks for subscribing!'); }} className="flex flex-col sm:flex-row gap-3">
            <input 
              required 
              type="email" 
              placeholder="Your email address" 
              className="flex-1 px-4 py-3 rounded-full border-2 border-teal-200 focus:border-teal-500 focus:outline-none"
            />
            <button className="px-8 py-3 bg-orange-500 text-white rounded-full font-bold hover:bg-orange-600 transition-all">
              Subscribe
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-teal-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Church className="w-8 h-8" />
                <Coffee className="w-6 h-6 -ml-2" />
                <span className="text-2xl font-black ml-2">HOPE CATHEDRAL</span>
              </div>
              <p className="text-teal-100">
                A Comedy TV Series by Dunamis - On-Stage Productions
              </p>
            </div>
            
            <div>
              <h3 className="font-bold mb-4 text-orange-300">Production Partners</h3>
              <ul className="space-y-2 text-teal-100">
                <li>Hekspace Studios</li>
                <li>Kherut Films</li>
                <li>Reel, Flicks & Art</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-bold mb-4 text-orange-300">Connect</h3>
              <div className="flex gap-3 mb-4">
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors">
                  <Youtube className="w-5 h-5" />
                </a>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
              <a href="mailto:hopecathedralshow@gmail.com?subject=Investment%20Deck%20Request" className="text-orange-300 hover:text-orange-200 block mb-2">Request Investment Deck</a>
              <a href="mailto:hopecathedralshow@gmail.com?subject=Screening%20Sponsorship" className="text-orange-300 hover:text-orange-200 block">Sponsor the Screening</a>
            </div>
          </div>
          
          <div className="text-center text-teal-100 pt-8 border-t border-teal-500">
            <div className="mb-3">
              <a href="https://www.kokoodi.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-200 hover:text-white transition-colors text-sm">
                <img src="./kokoodi-logo-white.png" alt="Kokoodi" className="h-4 opacity-75" />
                <span className="text-xs">AI conversations by Kokoodi</span>
              </a>
            </div>
            <div className="flex justify-center gap-4 mb-3 text-sm">
              <button onClick={() => setShowPrivacy(true)} className="text-teal-200 hover:text-white transition-colors">
                Privacy Policy
              </button>
              <span className="text-teal-400">•</span>
              <button onClick={() => setShowTerms(true)} className="text-teal-200 hover:text-white transition-colors">
                Terms & Conditions
              </button>
            </div>
            <p>© 2025 Hope Cathedral. All rights reserved.</p>
            <p className="text-orange-300 mt-2">Faith with a smile ☕</p>
          </div>
        </div>
      </footer>

      {/* Kokoodi mini Q&A */}
      <KokoodiMiniPane />

      {/* Hope Meter - Scroll Progress Cup */}
      <div className="hope-meter grid place-items-center p-3 text-white/90">
        <div className="meter-cup">
          <div className="meter-handle"></div>
          <div className="meter-fill" style={{ height: `${Math.round(scrollFill*100)}%` }} />
        </div>
      </div>

      {/* Privacy Policy Modal */}
      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}

      {/* Terms & Conditions Modal */}
      {showTerms && <TermsConditions onClose={() => setShowTerms(false)} />}

      {/* Investment Deck Request Modal */}
      {showInvestmentModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 md:p-8 shadow-2xl relative">
            <button
              onClick={() => {
                setShowInvestmentModal(false);
                setInvestmentStatus(null);
              }}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>

            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500 rounded-full mb-4">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-2">
                Request Investment Deck
              </h2>
              <p className="text-gray-600">
                Enter your details and we'll send you our comprehensive investment deck
              </p>
            </div>

            <form onSubmit={handleInvestmentDeckRequest} className="space-y-4">
              <div>
                <label htmlFor="inv-name" className="block text-sm font-bold text-gray-700 mb-2">
                  Your Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="inv-name"
                  value={investmentName}
                  onChange={(e) => setInvestmentName(e.target.value)}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="inv-email" className="block text-sm font-bold text-gray-700 mb-2">
                  Email Address <span className="text-gray-400 text-xs">(Optional)</span>
                </label>
                <input
                  type="email"
                  id="inv-email"
                  value={investmentEmail}
                  onChange={(e) => setInvestmentEmail(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="inv-mobile" className="block text-sm font-bold text-gray-700 mb-2">
                  Mobile Number <span className="text-gray-400 text-xs">(Optional)</span>
                </label>
                <input
                  type="tel"
                  id="inv-mobile"
                  value={investmentMobile}
                  onChange={(e) => setInvestmentMobile(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                  placeholder="+44 7700 900000"
                />
              </div>

              {investmentStatus && (
                <div className={`p-4 rounded-lg ${
                  investmentStatus.type === 'success' 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}>
                  <p className="text-sm font-medium">{investmentStatus.message}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={investmentSubmitting}
                className="w-full px-6 py-4 bg-gradient-to-r from-teal-500 to-orange-500 text-white rounded-lg font-black hover:from-teal-600 hover:to-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {investmentSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Request Investment Deck
                  </>
                )}
              </button>
            </form>

            <p className="text-xs text-gray-500 text-center mt-4">
              An investment deck request will be sent to our team. We'll respond within 24 hours.
            </p>
          </div>
        </div>
      )}
    </div>
    </LazyMotion>
  );
};

export default HopeCathedralLanding;